import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Music2, Minimize2, Maximize2, X, Radio } from 'lucide-react';
import DOMPurify from 'dompurify';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Context for radio state
const RadioContext = createContext(null);

// Hook to use radio context
export const useRadio = () => {
  const context = useContext(RadioContext);
  if (!context) {
    throw new Error('useRadio must be used within a RadioProvider');
  }
  return context;
};

// Memoized embed component - injects sanitized HTML via ref (avoids raw innerHTML)
const RadioEmbed = React.memo(({ embedCode }) => {
  const embedRef = useRef(null);
  const hasInjected = useRef(false);

  useEffect(() => {
    const currentRef = embedRef.current;
    
    if (currentRef && embedCode && !hasInjected.current) {
      // Sanitize and parse into DOM nodes instead of raw innerHTML
      const sanitizedHtml = DOMPurify.sanitize(embedCode, {
        ADD_TAGS: ['iframe'],
        ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'src'],
        RETURN_DOM_FRAGMENT: true,
      });
      currentRef.textContent = '';
      currentRef.appendChild(sanitizedHtml);
      hasInjected.current = true;
    }
  }, [embedCode]);

  return <div ref={embedRef} className="radio-embed-container" />;
});

RadioEmbed.displayName = 'RadioEmbed';

// Radio Provider Component
export const RadioProvider = ({ children }) => {
  const [radioState, setRadioState] = useState('closed'); // 'closed', 'open', 'minimized'
  const [radioSettings, setRadioSettings] = useState(null);

  // Fetch radio settings on mount
  useEffect(() => {
    const fetchRadioSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/radio`);
        setRadioSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch radio settings:', error);
      }
    };
    fetchRadioSettings();
  }, []);

  const openRadio = () => setRadioState('open');
  const closeRadio = () => setRadioState('closed');
  const minimizeRadio = () => setRadioState('minimized');
  const expandRadio = () => setRadioState('open');

  const toggleRadio = () => {
    if (radioState === 'closed') openRadio();
    else if (radioState === 'open') minimizeRadio();
    else expandRadio();
  };

  const isRadioActive = radioState !== 'closed';

  const value = {
    radioState,
    radioSettings,
    isRadioActive,
    openRadio,
    closeRadio,
    minimizeRadio,
    expandRadio,
    toggleRadio,
  };

  return (
    <RadioContext.Provider value={value}>
      {children}
      
      {/* Radio Player - Rendered at root level, persists across route changes */}
      {isRadioActive && radioSettings?.is_active && createPortal(
        <div 
          className="z-[9999] bg-black/95 backdrop-blur-xl border border-orange-500/30 shadow-2xl shadow-orange-500/20 overflow-hidden"
          style={{ 
            position: 'fixed',
            top: radioState === 'open' ? '80px' : 'auto',
            bottom: radioState === 'minimized' ? '16px' : 'auto',
            left: '16px',
            width: radioState === 'open' ? '320px' : '200px',
            maxWidth: 'calc(100vw - 32px)',
            borderRadius: '16px',
            transition: 'width 0.3s ease, top 0.3s ease, bottom 0.3s ease',
          }}
          data-testid="radio-player-container"
        >
          {/* Header */}
          <div 
            className="bg-gradient-to-r from-orange-500/20 to-green-500/20 p-2 border-b border-orange-500/20"
            style={{ transition: 'padding 0.3s ease' }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div 
                  className="rounded-full bg-gradient-to-r from-orange-500 to-green-500 flex items-center justify-center flex-shrink-0"
                  style={{ 
                    width: radioState === 'open' ? '32px' : '24px',
                    height: radioState === 'open' ? '32px' : '24px',
                    transition: 'width 0.3s ease, height 0.3s ease',
                  }}
                >
                  <Music2 
                    className={`text-white ${radioState === 'minimized' ? 'animate-pulse' : ''}`}
                    style={{ 
                      width: radioState === 'open' ? '16px' : '12px',
                      height: radioState === 'open' ? '16px' : '12px',
                    }}
                  />
                </div>
                <div className="min-w-0">
                  <h3 
                    className="text-white font-bold truncate"
                    style={{ 
                      fontSize: radioState === 'open' ? '14px' : '12px',
                      transition: 'font-size 0.3s ease',
                    }}
                  >
                    {radioState === 'open' ? (radioSettings.station_name || 'DharmaR Radio') : 'Radio'}
                  </h3>
                  <p className="text-green-400 flex items-center gap-1" style={{ fontSize: '12px' }}>
                    <span 
                      className="bg-green-500 rounded-full animate-pulse flex-shrink-0"
                      style={{ width: '6px', height: '6px' }}
                    />
                    <span style={{ fontSize: radioState === 'open' ? '12px' : '10px' }}>Live</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                {radioState === 'open' ? (
                  <button 
                    onClick={minimizeRadio}
                    className="text-gray-400 hover:text-orange-400 transition-colors p-1 hover:bg-orange-500/10 rounded-full"
                    title="Minimize"
                    data-testid="radio-minimize-btn"
                  >
                    <Minimize2 className="w-4 h-4" />
                  </button>
                ) : (
                  <button 
                    onClick={expandRadio}
                    className="text-gray-400 hover:text-orange-400 transition-colors p-1 hover:bg-orange-500/10 rounded-full"
                    title="Expand"
                    data-testid="radio-expand-btn"
                  >
                    <Maximize2 style={{ width: '12px', height: '12px' }} />
                  </button>
                )}
                <button 
                  onClick={closeRadio}
                  className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
                  title="Close"
                  data-testid="radio-close-btn"
                >
                  <X style={{ width: radioState === 'open' ? '16px' : '12px', height: radioState === 'open' ? '16px' : '12px' }} />
                </button>
              </div>
            </div>
          </div>

          {/* Embed container */}
          <div 
            style={{
              height: radioState === 'open' ? 'auto' : '60px',
              padding: radioState === 'open' ? '12px' : '4px',
              overflow: 'hidden',
              transition: 'height 0.3s ease, padding 0.3s ease',
            }}
          >
            <div
              style={{
                transform: radioState === 'minimized' ? 'scale(0.5)' : 'scale(1)',
                transformOrigin: 'top left',
                width: radioState === 'minimized' ? '200%' : '100%',
                height: radioState === 'minimized' ? '120px' : 'auto',
                overflow: 'hidden',
                transition: 'transform 0.3s ease',
              }}
            >
              {radioSettings.embed_code ? (
                <RadioEmbed embedCode={radioSettings.embed_code} />
              ) : (
                <div className="text-center py-4 text-gray-400">
                  <Radio className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Radio not configured</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </RadioContext.Provider>
  );
};

export default RadioContext;
